import type { ReactNode } from 'react'
import {
  CalendarDays,
  ClipboardList,
  Flame,
  Inbox,
  LayoutDashboard,
  LogOut,
  MapPin,
  CircleUser,
  Menu,
  Merge,
  Ticket,
  Users,
  X,
  Tags,
} from 'lucide-react'
import type { CmsUser } from '../types'
import { useState } from 'react'

export type CmsScreen = 'dashboard' | 'events' | 'review' | 'duplicates' | 'places' | 'producers' | 'sources' | 'popular' | 'team' | 'audit' | 'taxonomy'

type Props = {
  user: CmsUser
  categoryCatalogEnabled?: boolean
  screen: CmsScreen | 'editor' | 'place-editor' | 'producer-editor'
  // The list the event editor was opened from, kept highlighted.
  editorFrom?: CmsScreen
  onNavigate: (screen: CmsScreen) => void
  onSignOut: () => void
  children: ReactNode
}

// Grouped as the work goes: the catalog first, then what the app shows, where
// the automatic collection reads, and the team's own pages.
const navigation: { title: string | null; items: { id: CmsScreen; label: string; icon: typeof LayoutDashboard; roles?: CmsUser['role'][] }[] }[] = [
  { title: null, items: [{ id: 'dashboard', label: 'Visão geral', icon: LayoutDashboard }] },
  {
    title: 'Catálogo',
    items: [
      { id: 'events', label: 'Eventos', icon: CalendarDays },
      // Events imported from a link, checked before the app (PRD.md, item 93).
      { id: 'review', label: 'Para revisar', icon: Inbox, roles: ['owner', 'editor'] },
      { id: 'duplicates', label: 'Duplicados', icon: Merge, roles: ['owner'] },
      { id: 'places', label: 'Lugares', icon: MapPin },
      { id: 'producers', label: 'Perfis', icon: CircleUser },
    ],
  },
  { title: 'App', items: [{ id: 'popular', label: 'Popular', icon: Flame }, { id: 'taxonomy', label: 'Categorias e temas', icon: Tags, roles: ['owner'] }] },
  { title: 'Coleta automática', items: [{ id: 'sources', label: 'Fontes', icon: Ticket, roles: ['owner'] }] },
  {
    title: 'Administração',
    items: [
      { id: 'team', label: 'Equipe', icon: Users, roles: ['owner'] },
      // An editor sees only their own actions (PRD.md, item 45).
      { id: 'audit', label: 'Histórico', icon: ClipboardList, roles: ['owner', 'editor'] },
    ],
  },
]

const roleLabel = (role: CmsUser['role']) => (role === 'owner' ? 'Proprietário' : role === 'editor' ? 'Editor' : 'Leitor')

export function AppShell({ user, categoryCatalogEnabled = false, screen, editorFrom = 'events', onNavigate, onSignOut, children }: Props) {
  const [menuOpen, setMenuOpen] = useState(false)

  const navigate = (target: CmsScreen) => {
    onNavigate(target)
    setMenuOpen(false)
  }

  // An editor keeps its list highlighted.
  const isActive = (id: CmsScreen) => screen === id
    || (screen === 'editor' && id === editorFrom)
    || (screen === 'place-editor' && id === 'places')
    || (screen === 'producer-editor' && id === 'producers')

  return (
    <div className="app-shell">
      <aside className={`sidebar ${menuOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <img className="brand-logo" src="/guve-icon.png" alt="" width={28} height={28} />
          <strong>Guve</strong>
          <button className="icon-button sidebar__close" aria-label="Fechar menu" onClick={() => setMenuOpen(false)}>
            <X size={18} />
          </button>
        </div>

        <nav className="sidebar__nav" aria-label="Navegação principal">
          {navigation.map((group) => {
            const items = group.items.filter((item) => (!item.roles || item.roles.includes(user.role))
              && (item.id !== 'taxonomy' || categoryCatalogEnabled))
            if (!items.length) return null
            return (
              <div className="nav-group" key={group.title ?? 'home'}>
                {group.title && <span className="nav-group__title">{group.title}</span>}
                {items.map((item) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      className={`nav-item ${isActive(item.id) ? 'nav-item--active' : ''}`}
                      aria-current={isActive(item.id) ? 'page' : undefined}
                      onClick={() => navigate(item.id)}
                    >
                      <Icon size={18} strokeWidth={1.9} />
                      <span>{item.label}</span>
                    </button>
                  )
                })}
              </div>
            )
          })}
        </nav>

        <div className="sidebar__account">
          <span className="account-avatar" aria-hidden="true">{user.email.slice(0, 1).toUpperCase()}</span>
          <span className="account-copy">
            <strong>{user.email}</strong>
            <small>{roleLabel(user.role)}</small>
          </span>
          <button className="icon-button" aria-label="Sair" title="Sair" onClick={onSignOut}><LogOut size={17} /></button>
        </div>
      </aside>

      {menuOpen && <button className="sidebar-backdrop" aria-label="Fechar menu" onClick={() => setMenuOpen(false)} />}

      <div className="app-content">
        <header className="topbar">
          <button className="icon-button topbar__menu" aria-label="Abrir menu" onClick={() => setMenuOpen(true)}>
            <Menu size={20} />
          </button>
          <img className="brand-logo" src="/guve-icon.png" alt="" width={24} height={24} />
          <strong>Guve</strong>
        </header>
        <main className="main-content">{children}</main>
      </div>
    </div>
  )
}
